import React from "react";

import {
  CardCta,
  CardNeutralState,
  CheckIcon,
  MetaRow,
  OverviewCard,
  ReceiptIcon,
} from "./overviewUi";

/**
 * "Finanzen".
 *
 * ONE CARD, TWO WORLDS, AND IT NAMES THE ONE IT IS IN (TINV-2).
 *
 * Until TINV-2 this card read the native Vantio `invoices` table and nothing
 * else — its own docstring said so, and gave a good reason at the time: a freshly
 * synced Bexio invoice carries no tenant mapping, so a tenant-scoped query over
 * the mirror found nothing and its silence would have read as "no debt". The
 * reason has expired. TINV-1 answers the tenant question properly, through the
 * draft ledger as well as the mapping column, so the mirror's silence is now
 * real silence. What remained was the worse half of the old arrangement: for an
 * organization whose `invoice_creation_provider` is `bexio`, the native table
 * stops gaining rows entirely, and this card told such a tenant's manager that
 * nothing had ever been invoiced.
 *
 * WHAT IT MAY SAY, PER WORLD:
 *
 *   native  UNCHANGED, deliberately. Each row's `status` is the backend's
 *           `effective_status`, resolved against the Swiss business day — so an
 *           overdue row is a fact. But the loaded window is the newest twenty
 *           and overdue invoices are by definition old, so a COUNT is stated
 *           only when the window provably holds every invoice. "Alle Rechnungen
 *           sind bezahlt" is never printed on an unproven basis: fewer loaded
 *           rows than exist would render an unpaid tenant as settled, which is
 *           the worst error this surface can make.
 *
 *   bexio   A COUNT AND NOTHING ELSE. The Bexio wire carries `bexio_status` —
 *           the PROVIDER's word — and no canonical `effective_status`. There is
 *           therefore no honest basis for "keine überfälligen Rechnungen", "X
 *           überfällig" or "alles bezahlt", and this card invents none. It says
 *           how many invoices Bexio holds for this person, names the world, and
 *           sends the reader to the tab that shows them. A neutral true
 *           sentence beats a reassuring invented one.
 *
 *   unknown While the capability read is in flight, and after it failed, NO
 *           world is named and no figure is shown. Falling back to Vantio would
 *           reintroduce exactly the untruth above, and calling the unknown world
 *           "aktuell" or "Historie" would rank two worlds without knowing which
 *           is which.
 *
 * THE SOURCE LINE APPEARS ONLY FOR BEXIO, and that asymmetry is intentional
 * rather than an oversight: the native body already carries the words "in
 * Vantio", so a "Quelle: Vantio aktuell" row beside it would state the same fact
 * twice, and adding it would change a card the brief asks to leave alone.
 *
 * No "Nächste Rechnung" in either world: no next-invoice logic exists, and
 * `issue_date`/`due_date` are explicitly not a service period. "Bexio verbunden"
 * is likewise absent — that connection is per organisation, so it would read
 * identically for every tenant.
 */
export default function FinanceCard({ invoices, onOpen }) {
  const source = invoices?.source ?? null;
  const loading = invoices?.loading === true;
  const error = invoices?.error ?? null;
  const total = invoices?.total;

  const complete = invoices?.complete === true;
  const overdue = invoices?.overdueCount;
  const unpaid = invoices?.unpaidCount;

  const noInvoices = complete && total === 0;
  const allSettled = complete && total > 0 && overdue === 0 && unpaid === 0;

  return (
    <OverviewCard
      title="Finanzen"
      icon={<ReceiptIcon />}
      accent="amber"
      testId="overview-finance-card"
      footer={
        onOpen ? (
          <CardCta onClick={onOpen} testId="overview-finance-cta">
            Zu den Rechnungen
          </CardCta>
        ) : null
      }
    >
      {error ? (
        <CardNeutralState
          testId="overview-finance-error"
          text="Rechnungen konnten nicht geladen werden."
        />
      ) : loading || !source ? (
        // No world, no claim — and no word that ranks the two worlds while
        // which of them is current is still unknown.
        <CardNeutralState
          testId="overview-finance-pending"
          text="Rechnungen werden geladen …"
        />
      ) : source === "bexio" ? (
        total === 0 ? (
          <p
            className="m-0 text-[13px] leading-snug text-muted-foreground"
            data-testid="overview-finance-none-bexio"
          >
            Für diesen Mieter sind in Bexio noch keine Rechnungen erfasst.
          </p>
        ) : (
          <>
            <p
              className="m-0 text-[13px] leading-snug text-card-foreground"
              data-testid="overview-finance-bexio"
            >
              {Number.isFinite(Number(total))
                ? total === 1
                  ? "1 Rechnung in Bexio"
                  : `${total} Rechnungen in Bexio`
                : "Rechnungen in Bexio"}
            </p>
            <p className="m-0 mt-1 text-[11.5px] leading-snug text-muted-foreground">
              Aktuelle Rechnungsdetails im Tab «Rechnungen».
            </p>
            <div className="mt-3 divide-y divide-border">
              <MetaRow label="Quelle" value={invoices?.sourceLabel} />
            </div>
          </>
        )
      ) : noInvoices ? (
        <p
          className="m-0 text-[13px] leading-snug text-muted-foreground"
          data-testid="overview-finance-none"
        >
          Für diesen Mieter sind in Vantio noch keine Rechnungen erfasst.
        </p>
      ) : complete ? (
        <>
          <div
            className="flex items-start gap-2.5"
            data-testid="overview-finance-overdue"
          >
            {overdue === 0 ? (
              <>
                <span
                  aria-hidden
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
                >
                  <CheckIcon />
                </span>
                <span className="text-[13.5px] font-medium text-emerald-700 dark:text-emerald-300">
                  Keine überfälligen Rechnungen
                </span>
              </>
            ) : (
              <>
                <span
                  aria-hidden
                  className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-rose-100 dark:bg-rose-500/15"
                />
                <span className="text-[13.5px] font-medium text-rose-700 dark:text-rose-300">
                  {overdue === 1
                    ? "1 überfällige Rechnung"
                    : `${overdue} überfällige Rechnungen`}
                </span>
              </>
            )}
          </div>

          <div className="mt-3 divide-y divide-border">
            <MetaRow label="Offen" value={String(unpaid)} />
            <MetaRow label="Rechnungen gesamt" value={String(total)} />
          </div>

          {allSettled ? (
            <p className="m-0 pt-3 text-[11.5px] leading-snug text-muted-foreground">
              Bezieht sich auf die in Vantio erfassten Rechnungen.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <p
            className="m-0 text-[13px] leading-snug text-card-foreground"
            data-testid="overview-finance-partial"
          >
            {Number.isFinite(Number(total))
              ? `${total} Rechnungen erfasst.`
              : "Rechnungen erfasst."}
          </p>
          <p className="m-0 mt-1 text-[11.5px] leading-snug text-muted-foreground">
            Der Zahlungsstand wird im Tab «Rechnungen» vollständig angezeigt.
          </p>
        </>
      )}
    </OverviewCard>
  );
}
